/**
 * Wishlist — Majid Alyousef 2026
 * ================================
 * Listens on .pcrd__wishlist-btn buttons (data-url attribute).
 * Unified storage: same key ('ma-wishlist') and format (product URLs) as
 * ma-wishlist.js and ma-wishlist-page.liquid — so items added from product
 * cards show up on the wishlist page, and vice-versa.
 *
 * Mobile: touchstart/touchend with preventDefault stop the parent <a>
 * link from firing when the heart is tapped.
 */

const Wishlist = (() => {
  const STORAGE_KEY = 'ma-wishlist';  // unified key — same as ma-wishlist.js

  // ── URL normalisation (mirrors maUrlNorm in ma-wishlist.js) ──────

  function normUrl(raw) {
    try {
      const path = new URL(raw, window.location.origin).pathname;
      const m = path.match(/\/products\/[^/?#]+/);
      return m ? m[0] : path;
    } catch {
      const m = String(raw).match(/\/products\/[^/?#]+/);
      return m ? m[0] : raw;
    }
  }

  // ── Read / Write ──────────────────────────────────────────────

  function getUrls() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  }

  function setUrls(urls) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(urls));
  }

  function has(url) { return getUrls().includes(normUrl(url)); }

  function toggle(url) {
    const nu = normUrl(url);
    const urls = getUrls();
    const idx = urls.indexOf(nu);
    if (idx === -1) { urls.push(nu); setUrls(urls); return true; }
    else            { urls.splice(idx, 1); setUrls(urls); return false; }
  }

  // ── UI ────────────────────────────────────────────────────────

  function syncButton(btn) {
    const url = btn.dataset.url;
    if (!url) return;
    const wishlisted = has(url);
    btn.classList.toggle('is-wishlisted', wishlisted);
    btn.setAttribute('aria-label', wishlisted ? 'Remove from wishlist' : 'Add to wishlist');
  }

  function syncAll() {
    document.querySelectorAll('.pcrd__wishlist-btn').forEach(syncButton);
  }

  // ── Binding ───────────────────────────────────────────────────
  // Direct binding — avoids document delegation being blocked by
  // stopPropagation on card link clicks.

  function animateHeart(btn) {
    btn.classList.remove('is-tapping');
    void btn.offsetWidth;  // force reflow to restart animation
    btn.classList.add('is-tapping');
    btn.addEventListener('animationend', () => btn.classList.remove('is-tapping'), { once: true });
  }

  function handleWishlistAction(btn, e) {
    e.preventDefault();
    e.stopPropagation();
    const url = btn.dataset.url;
    if (!url) return;
    toggle(url);
    syncButton(btn);
    animateHeart(btn);
  }

  function bindButton(btn) {
    if (btn.dataset.wlBound) return;  // prevent double-binding
    btn.dataset.wlBound = '1';

    // touchstart: preventDefault stops the browser from also firing a
    // link-click on any ancestor <a>, which is what broke mobile.
    btn.addEventListener('touchstart', function (e) {
      e.preventDefault();
      e.stopPropagation();
    }, { passive: false });

    btn.addEventListener('touchend', function (e) {
      e.preventDefault();
      e.stopPropagation();
      handleWishlistAction(this, e);
    }, { passive: false });

    // click handles mouse / keyboard / non-touch fallback
    btn.addEventListener('click', function (e) {
      if (this.dataset.touchHandled) { delete this.dataset.touchHandled; return; }
      handleWishlistAction(this, e);
    });
  }

  function bindAll() {
    document.querySelectorAll('.pcrd__wishlist-btn').forEach(bindButton);
  }

  // ── Init ──────────────────────────────────────────────────────

  function init() {
    bindAll();
    syncAll();
    // Re-bind + re-sync when Shopify re-renders sections
    document.addEventListener('shopify:section:load', () => { bindAll(); syncAll(); });
  }

  return { init, has, toggle, getUrls };
})();

document.addEventListener('DOMContentLoaded', Wishlist.init);
